import { Component, OnInit, computed, inject, input, model, output, signal } from '@angular/core';
import { Router } from '@angular/router';

import { I18nService } from '../../../services/i18n.service';
import { NotificationService } from '../../../services/notification.service';
import { PushPermissionStatus } from '../../../stores/notification.store';
import { AssetNavButtonComponent } from '../../../../public-api';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { SpinnerComponent } from '../../../../public-api';
import { UiService } from '../../../services/ui.service';
import { EmailToggleComponent } from '../email-toggle/email-toggle';
import { PushNotificationToggleComponent } from '../push-notification-toggle/push-notification-toggle';
import { NzIconModule } from 'ng-zorro-antd/icon';

enum EquipmentType {
  none = 0,
  flange,
  pipe,
  steamtrap,
  rotating,
}

@Component({
  selector: 'app-notifications-settings-subpage',
  templateUrl: './notifications-subpage.html',
  styleUrls: ['./notifications-subpage.scss'],
  imports: [
    AssetNavButtonComponent,
    EmailToggleComponent,
    PushNotificationToggleComponent,
    NzSpinModule,
    NzModalModule,
    SpinnerComponent,
    NzIconModule,
  ],
})
export class NotificationsSettingsSubpageComponent implements OnInit {
  // --- Services ---
  public router = inject(Router);
  public ui = inject(UiService);
  public i18n = inject(I18nService);
  private notification = inject(NotificationService);

  // --- Inputs / Models ---
  public settings = model<any>(); // model allows two-way signal sync
  public error = input<boolean>(false);
  public loading = input<boolean>(false);
  public success = input<boolean>(false);

  // --- Outputs ---
  public updateSettings = output<any>();

  // --- UI State Signals ---
  public permission_error = signal<boolean>(false);
  public selectedType = signal<EquipmentType>(EquipmentType.none);
  public requestingNotificationPermission = signal<boolean>(false);
  public notificationPermissionStatus = signal<PushPermissionStatus>(
    this.notification.PermissionStatus
  );
  public pushNotificationModalVisible = signal<boolean>(false);

  public reset = signal<boolean>(false);
  public customErrorMessage = signal<string | null>(null);
  public customAlertTitle = signal<string | null>(null);
  public customAlertMessage = signal<string | null>(null);
  public instructionsAddToHomeScreenIOS = signal<boolean>(false);
  private isClosingPushNotificationModal = false;

  // --- Callbacks (Non-Signal) ---
  public pushNotificationModalResolve: ((value: void | PromiseLike<void>) => void) | null = null;
  public pushNotificationModalReject: ((reason?: any) => void) | null = null;

  // --- Computed Signals (Replacing Getters) ---

  public IsTypeSelected = computed(() => this.selectedType() !== EquipmentType.none);
  public IsSteamtrap = computed(() => this.selectedType() === EquipmentType.steamtrap);
  public IsRotatingEq = computed(() => this.selectedType() === EquipmentType.rotating);
  public UserEmail = computed(() => this.settings()?.email ?? '');

  // Dynamic Computed Signals for the UI
  // Use these in your template like: [checked]="alarmEmailEnabled()"

  public alarmEmailEnabled = computed(() => this.getSettingValue('Email', 'Alarm'));
  public alarmPushEnabled = computed(() => this.getSettingValue('Push', 'Alarm'));

  public warningEmailEnabled = computed(() => this.getSettingValue('Email', 'Warning'));
  public warningPushEnabled = computed(() => this.getSettingValue('Push', 'Warning'));

  public inactiveEmailEnabled = computed(() => this.getSettingValue('Email', 'Inactive'));
  public inactivePushEnabled = computed(() => this.getSettingValue('Push', 'Inactive'));

  public workingEmailEnabled = computed(() => this.getSettingValue('Email', 'Working'));
  public workingPushEnabled = computed(() => this.getSettingValue('Push', 'Working'));

  // --- Helper for Computed logic ---
  private getSettingValue(channel: 'Email' | 'Push', category: string): boolean {
    const s = this.settings();
    const t = this.selectedType();
    if (!s || t === EquipmentType.none) return false;

    const mapping: Record<number, string> = {
      [EquipmentType.flange]: `notificationsFlange${category}${channel}`,
      [EquipmentType.pipe]: `notificationsPipe${category}${channel}`,
      [EquipmentType.steamtrap]: `notificationsSteamtrap${category}${channel}`,
      [EquipmentType.rotating]:
        category === 'Alarm'
          ? `notificationsNanoCritical${channel}`
          : `notificationsNano${category}${channel}`,
    };

    return s[mapping[t]];
  }

  public updateEmailSetting(
    category: 'Alarm' | 'Warning' | 'Inactive' | 'Working',
    value: boolean
  ) {
    this.updateBaseSetting('Email', category, value);
  }

  public updatePushSetting(category: 'Alarm' | 'Warning' | 'Inactive' | 'Working', value: boolean) {
    if (value === true) {
      this.triggerNotificationPermissionPrompt().then(
        () => this.updateBaseSetting('Push', category, value),
        () => this.reset.update((v) => !v)
      );
    } else {
      this.updateBaseSetting('Push', category, value);
    }
  }

  private updateBaseSetting(channel: 'Email' | 'Push', category: string, value: boolean) {
    const s = { ...this.settings() };
    const t = this.selectedType();
    if (!s || t === EquipmentType.none) return;

    const mapping: Record<number, string> = {
      [EquipmentType.flange]: `notificationsFlange${category}${channel}`,
      [EquipmentType.pipe]: `notificationsPipe${category}${channel}`,
      [EquipmentType.steamtrap]: `notificationsSteamtrap${category}${channel}`,
      [EquipmentType.rotating]:
        category === 'Alarm'
          ? `notificationsNanoCritical${channel}`
          : `notificationsNano${category}${channel}`,
    };

    s[mapping[t]] = value;
    this.settings.set(s); // Update the signal
    this.updateSettings.emit(s); // Emit the updated settings
  }
  readonly isSafari = signal<boolean>(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));

  ngOnInit() {
    this.refresh();
  }

  refresh() {
    const urlParts = this.router.url.replace('/settings/', '').split('/');
    const path = urlParts.length > 1 ? urlParts[1] : '';
    switch (path) {
      case 'flange':
        this.selectedType.set(EquipmentType.flange);
        break;
      case 'pipe':
        this.selectedType.set(EquipmentType.pipe);
        break;
      case 'steamtrap':
        this.selectedType.set(EquipmentType.steamtrap);
        break;
      case 'rotating':
        this.selectedType.set(EquipmentType.rotating);
        break;
      default:
        this.selectedType.set(EquipmentType.none);
    }
  }

  getTitle() {
    const type = this.selectedType();
    const suffix = this.i18n.langString('notifications');
    switch (type) {
      case EquipmentType.none:
        return suffix;
      case EquipmentType.flange:
        return `${this.i18n.langString('flange')} ${suffix}`;
      case EquipmentType.pipe:
        return `${this.i18n.langString('pipe')} ${suffix}`;
      case EquipmentType.steamtrap:
        return `${this.i18n.langString('steamtrap_non_abbrev')} ${suffix}`;
      case EquipmentType.rotating:
        return `${this.i18n.langString('rotatingequipment')} ${suffix}`;
    }
  }

  public SelectFlange() {
    this.router.navigate(['settings', 'notifications', 'flange']);
  }
  public SelectPipe() {
    this.router.navigate(['settings', 'notifications', 'pipe']);
  }
  public SelectSteamtrap() {
    this.router.navigate(['settings', 'notifications', 'steamtrap']);
  }
  public SelectRotatingEq() {
    this.router.navigate(['settings', 'notifications', 'rotating']);
  }

  public requestNotificationPermission() {
    this.permission_error.set(false);
    this.requestingNotificationPermission.set(true);
    this.notification.subscribeToNotifications(
      (status: PushPermissionStatus) => {
        this.requestingNotificationPermission.set(false);
        if (status !== PushPermissionStatus.granted) {
          this.notificationPermissionStatus.set(status);

          if (status === PushPermissionStatus.denied) {
            this.permission_error.set(true);
          }

          this.closePushNotificationModal(false);
        } else {
          this.notificationPermissionStatus.set(PushPermissionStatus.granted);
          this.closePushNotificationModal(true);
        }
      },
      (_reason) => {
        this.permission_error.set(true);
        this.requestingNotificationPermission.set(false);
        this.notificationPermissionStatus.set(PushPermissionStatus.denied);
        this.closePushNotificationModal(false);
      }
    );
  }

  private triggerNotificationPermissionPrompt(): Promise<void> {
    return new Promise((resolve, reject) => {
      let isResolvedOrRejected = false;
      try {
        if (!('Notification' in window)) {
          if (this.isSafari()) {
            this.customAlert(
              this.i18n.langString('notifications_not_supported_title'),
              this.i18n.langString('safari_not_supporting_notifications'),
              true
            );
          } else {
            this.customAlert(
              this.i18n.langString('notifications_not_supported_title'),
              this.i18n.langString('browser_not_supporting_notifications')
            );
          }
          reject();
          isResolvedOrRejected = true;
        } else if (Notification.permission !== 'granted') {
          this.displayNotificationPermissionPrompt(resolve, reject);
          isResolvedOrRejected = true;
        } else {
          resolve();
          isResolvedOrRejected = true;
        }
      } catch (error: any) {
        this.customError(this.i18n.langString('error_triggering_notification_prompt') + error.message);
        reject();
        isResolvedOrRejected = true;
      }
      if (!isResolvedOrRejected) {
        this.customError(
          this.i18n.langString('error_triggering_notification_prompt') + 'unknown reason'
        );
        reject();
      }
    });
  }

  private displayNotificationPermissionPrompt(resolve: any, reject: any) {
    try {
      this.pushNotificationModalResolve = resolve;
      this.pushNotificationModalReject = reject;
      this.pushNotificationModalVisible.set(true);
    } catch (error: any) {
      this.customError(this.i18n.langString('error_displaying_notification_prompt') + error.message);
      reject();
    }
  }

  public closePushNotificationModal(resolve: boolean) {
    if (this.isClosingPushNotificationModal) {
      return;
    }

    this.isClosingPushNotificationModal = true;

    const resolveCallback = this.pushNotificationModalResolve;
    const rejectCallback = this.pushNotificationModalReject;

    // Close and reset modal state first to avoid race conditions with modal events.
    this.pushNotificationModalVisible.set(false);
    this.requestingNotificationPermission.set(false);
    this.pushNotificationModalResolve = null;
    this.pushNotificationModalReject = null;

    try {
      if (resolve) {
        resolveCallback?.();
      } else {
        rejectCallback?.();
      }
    } catch (error: any) {
      this.customError(this.i18n.langString('error_solving_notification_prompt') + error.message);
      rejectCallback?.();
    } finally {
      this.isClosingPushNotificationModal = false;
    }
  }

  public customError(message: string) {
    this.customErrorMessage.set(message);
  }

  public DisplayAddToHomeScreenIOSInstructions() {
    this.customAlert(
      this.i18n.langString('instructions'),
      this.i18n.langString('add_to_home_screen_instructions_ios'),
      false
    );
  }

  public customAlert(title: string, message: string, instructionsLink = false) {
    this.customAlertTitle.set(title);
    this.customAlertMessage.set(message);
    this.instructionsAddToHomeScreenIOS.set(instructionsLink);
  }

  public closeCustomAlert() {
    this.customAlertMessage.set(null);
    this.instructionsAddToHomeScreenIOS.set(false);
  }
}
